import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { bffQuery, bffMutation } from '../lib/bffClient.js';
import { refreshAccessToken, redirectToLogin } from '../lib/auth.js';
import {
  setActiveClinicRef, getActiveClinicRef, readLastClinic, rememberLastClinic,
  resetAccessVersion, onAccessVersionChange, guardedSwitch,
} from '../lib/clinicSignal.js';
import { resetPaywall } from '../lib/paywallSignal.js';
import { clinicRefOfPath } from '../hooks/useAppNavigate.js';
import { CurrencyProvider, clinicCurrencyOf } from '../i18n';
import { DEFAULT_TIMEZONE, setClinicZone } from '../lib/clinicZone';

/**
 * The active clinic, the person's memberships, and their access in that clinic.
 *
 * docs/CLINIC_MULTI_CLINIC_UX_REV2.md. Three ideas hold this together:
 *
 * 1. THE ROUTE IS THE TENANT. `/c/:clinicRef/…`. This provider does not own the
 *    active clinic — it reads it from the route and mirrors it into the transport.
 *    Nothing stores it, so two tabs are two clinics for free and a bookmark is
 *    always right.
 * 2. PERMISSIONS COME FROM THE SERVER, per clinic. `myAccess` is resolved by the
 *    BFF for the clinic in the header, so the sidebar and the boundary cannot
 *    disagree, and a switch changes both together.
 * 3. FIVE BOOT OUTCOMES, NOT TWO. «the query failed» and «this person genuinely
 *    belongs nowhere» must never collapse — the existing App.jsx comment records
 *    what that cost the last time (a database blip told a three-year clinic owner
 *    to go create their clinic), and the same reasoning extends to «waiting on an
 *    invitation», which is a third thing again.
 */

const BOOTSTRAP = `
  query ClinicBootstrap {
    platformCapabilities { multiFounder }
    myMemberships {
      clinicId slug clinicName city currency timezone
      kinds status tier
      roles { id nameFa color }
      enterable blockedReason isFounder repairNeeded
      joinedAt invitedBy invitedAt lastActiveAt
    }
    myAccess {
      clinicId tier permissions version noPermissions practitionerId
      roles { id nameFa color }
    }
  }
`;

const REPAIR = `
  mutation RepairClinicSetup($clinicId: ID!) {
    repairClinicSetup(clinicId: $clinicId) { clinicId repairNeeded enterable }
  }
`;

/** The five outcomes of §5.1. Each is an answer, never a failure in disguise. */
export const BOOT = {
  LOADING: 'loading',
  READY: 'ready',
  NONE: 'none',
  PENDING_ONLY: 'pending-only',
  ERROR: 'error',
};

/**
 * Run one request with no `x-active-clinic-id` on it, then put the ref back.
 *
 * `clinicSignal` is a module singleton the fetch layer reads, so this is the only
 * way to ask a person-scoped question while the URL names a clinic. The window is
 * one awaited request during boot, when the gate is still holding every child
 * unmounted, so nothing else is in flight to lose its header.
 */
export async function withoutClinicHeader(run) {
  const named = getActiveClinicRef();
  setActiveClinicRef('');
  try {
    return await run();
  } finally {
    setActiveClinicRef(named);
  }
}

const ClinicCtx = React.createContext(null);

/**
 * The context, or null outside a provider — for a screen that can use it (to
 * refresh memberships after changing the clinic's currency) but must also render
 * in isolation, as its tests mount it.
 */
export function useOptionalClinicContext() {
  return React.useContext(ClinicCtx);
}

export function useClinicContext() {
  const ctx = React.useContext(ClinicCtx);
  if (!ctx) throw new Error('useClinicContext must be used inside <ClinicContextProvider>');
  return ctx;
}

/** Match a membership by slug OR id — the route accepts either (§A1). */
export const matchRef = (memberships, ref) =>
  (memberships || []).find((m) => m.slug === ref || m.clinicId === ref) || null;

/** The canonical ref for a URL: slug when the migration has landed, else the id. */
export const refOf = (m) => (m ? (m.slug || m.clinicId) : '');

export function ClinicContextProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  // FROM THE PATH, not from `useParams()`.
  //
  // This provider is mounted above the `Routes` — it has to be, because `/start`
  // and `/onboarding` need it too — and `useParams()` reports only the params of
  // routes matched at or ABOVE the calling component. So `params.clinicRef` was
  // always `undefined` here however specific the URL was, which made `routeRef`
  // permanently '', `activeMembership` permanently null, and ClinicGate refuse
  // every clinic screen to every person with «این کلینیک در دسترس شما نیست» —
  // including a clinic's own founder. It also meant `x-active-clinic-id` was never
  // sent, so the BFF silently fell back to its inference ladder and none of the
  // multi-clinic validation this design turns on was actually reached.
  const routeRef = React.useMemo(
    () => clinicRefOfPath(location.pathname),
    [location.pathname],
  );

  const [state, setState] = React.useState(BOOT.LOADING);
  const [memberships, setMemberships] = React.useState([]);
  // What the PLATFORM can do, as opposed to what this person may do. Read in
  // the same document as the memberships because the registration wizard needs
  // it before it can decide whether to offer a form at all, and a second round
  // trip at that moment would be a form that flickers.
  const [capabilities, setCapabilities] = React.useState({ multiFounder: false });
  const [access, setAccess] = React.useState(null);
  const [error, setError] = React.useState('');
  const [switching, setSwitching] = React.useState('');
  const [accessChanged, setAccessChanged] = React.useState(false);

  // The transport must know the clinic BEFORE the first request of a route goes
  // out, so this is a layout effect rather than an effect: an ordinary effect
  // runs after paint, and a child's mount-time query would already have been
  // sent with the previous clinic's header.
  React.useLayoutEffect(() => { setActiveClinicRef(routeRef); }, [routeRef]);

  const load = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setState(BOOT.LOADING);
    try {
      let data;
      try {
        data = await bffQuery(BOOTSTRAP, {}, 'ClinicBootstrap');
      } catch (err) {
        if (err.extensions?.code !== 'CLINIC_FORBIDDEN') throw err;
        // ASK AGAIN WITHOUT NAMING A CLINIC.
        //
        // The BFF refuses a clinic the caller does not belong to in its `context`,
        // BEFORE any resolver runs — so the refusal lands on every operation in the
        // request, this bootstrap included. That is the right call for clinic-scoped
        // data and the wrong outcome here: the one query that could tell this person
        // which clinics they DO belong to is the query being refused, leaving an
        // empty list, a «not a member» wall, and no clinic to offer as a way out.
        //
        // The answer is person-scoped, so asking for it without a clinic is always
        // safe and never widens what is served. The gate then renders the SAME named
        // 403 — but with a working button to a clinic they really do have.
        data = await withoutClinicHeader(() => bffQuery(BOOTSTRAP, {}, 'ClinicBootstrap'));
      }
      const list = data?.myMemberships || [];
      setMemberships(list);
      // Absent (an older BFF) reads as false, which is the safe direction: the
      // wizard refuses a second clinic with an explanation rather than walking
      // a founder into a `clinics_pkey` collision.
      setCapabilities({ multiFounder: data?.platformCapabilities?.multiFounder === true });
      setAccess(data?.myAccess || null);
      resetAccessVersion(data?.myAccess?.version || '');
      setAccessChanged(false);
      setError('');

      if (!list.length) { setState(BOOT.NONE); return list; }
      if (!list.some((m) => m.enterable)) { setState(BOOT.PENDING_ONLY); return list; }
      setState(BOOT.READY);
      return list;
    } catch (err) {
      if (err.extensions?.code === 'UNAUTHENTICATED' || err.status === 401) {
        redirectToLogin();
        return [];
      }
      // A clinic the caller does not belong to is NOT a boot failure — the panel
      // is fine, this URL is not. Reached only when the retry above ALSO failed.
      if (err.extensions?.code === 'CLINIC_FORBIDDEN') {
        setError('');
        setState(BOOT.READY);
        return memberships;
      }
      console.warn('[ClinicContext] bootstrap failed:', err.message);
      setError(err.message || '');
      setState(BOOT.ERROR);
      return [];
    }
  }, [memberships]);

  React.useEffect(() => { load(); /* once */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-resolve access when the route names a different clinic. The membership list
  // is person-scoped and does not change; `myAccess` is clinic-scoped and does.
  const lastResolved = React.useRef('');
  React.useEffect(() => {
    if (state !== BOOT.READY || !routeRef) return;
    if (lastResolved.current === routeRef) return;
    lastResolved.current = routeRef;
    if (access?.clinicId && matchRef(memberships, routeRef)?.clinicId === access.clinicId) return;
    load({ silent: true });
  }, [routeRef, state, memberships, access, load]);

  // A role change reaches an already-open session here (§5.10, §A8). Non-blocking
  // on purpose: a receptionist halfway through an invoice decides when the ground
  // moves, and a silent re-render under their hands is the wrong answer.
  React.useEffect(() => onAccessVersionChange(() => setAccessChanged(true)), []);

  const activeMembership = React.useMemo(
    () => matchRef(memberships, routeRef),
    [memberships, routeRef],
  );

  React.useEffect(() => {
    if (activeMembership?.enterable) rememberLastClinic(refOf(activeMembership));
  }, [activeMembership]);

  const enterable = React.useMemo(() => memberships.filter((m) => m.enterable), [memberships]);

  // The currency the active clinic keeps its books in (docs/clinic-currency/
  // CONTRACTS.md §10). Every clinic-money figure on every screen is formatted,
  // labelled and parsed in it — <CurrencyProvider> below hands it to
  // `useTranslation()`. No clinic in the route (the switcher, onboarding) and an
  // older BFF without the field both read IRR, which is what every clinic was.
  const currency = React.useMemo(
    () => clinicCurrencyOf(activeMembership?.currency),
    [activeMembership],
  );

  // …and the wall clock it works by (PM-0088). Every date, time and «امروز» of
  // this clinic is on it — the formatters through <CurrencyProvider timeZone>,
  // the date arithmetic (dateRange.js, the booking grid) through the clinicZone
  // mirror. Mirrored DURING render, not in an effect: a child computing «امروز»
  // on its first render must already see this clinic's clock. Idempotent.
  const timeZone = activeMembership?.timezone || DEFAULT_TIMEZONE;
  setClinicZone(timeZone);

  /**
   * Where should a bare `/` go? (§A3 — and note what is NOT here: the switcher
   * never auto-opens. `myMemberships` is ordered most-recently-active first by the
   * server, so even a fresh browser lands somewhere sensible.)
   */
  const defaultRef = React.useMemo(() => {
    const remembered = matchRef(enterable, readLastClinic());
    return refOf(remembered || enterable[0] || null);
  }, [enterable]);

  /**
   * Switch clinics.
   *
   * Revert-on-failure is the property that matters: validation happens BEFORE the
   * URL moves, so a switch that cannot proceed leaves the person exactly where they
   * were rather than stranded on a route that will not resolve.
   */
  const switchClinic = React.useCallback(async (ref) => {
    const target = matchRef(memberships, ref);
    if (!target || !target.enterable) return false;
    if (refOf(target) === routeRef) return true;

    setSwitching(refOf(target));
    // Asks the open screen first. A switch discards the tree by design, but
    // discarding a half-typed patient without a word is not the same thing as
    // discarding stale data — and it is the same keystroke, so the screen with the
    // work open is the only thing that can tell them apart (§A11).
    guardedSwitch(() => {
      // Clinic-scoped module state that the tree remount cannot reach on its own.
      // The paywall is the sharp one: it is a module singleton, so switching from
      // a lapsed clinic to a paid one would carry the dialog across (E11).
      resetPaywall();
      resetAccessVersion('');
      rememberLastClinic(refOf(target));
      navigate(`/c/${refOf(target)}/`, { replace: false });
    });
    setSwitching('');
    return true;
  }, [memberships, routeRef, navigate]);

  /**
   * Finish a half-applied createClinic saga (§A9), then refresh the token.
   *
   * The refresh is not optional: the repair grants a Keycloak role, and the
   * session is holding a token issued before that grant existed. Without it the
   * very next request is FORBIDDEN and the repair looks like it failed.
   */
  const repairSetup = React.useCallback(async (clinicId) => {
    await bffMutation(REPAIR, { clinicId }, 'RepairClinicSetup');
    await refreshAccessToken();
    return load({ silent: true });
  }, [load]);

  /** Adopt new grants after accepting an invitation or creating a clinic (§A8). */
  const refreshGrants = React.useCallback(async () => {
    await refreshAccessToken();
    return load({ silent: true });
  }, [load]);

  const value = React.useMemo(() => ({
    state,
    memberships,
    capabilities,
    enterable,
    activeMembership,
    currency,
    timeZone,
    activeRef: routeRef,
    defaultRef,
    access,
    error,
    switching,
    accessChanged,
    dismissAccessChange: () => setAccessChanged(false),
    canSwitch: enterable.length > 1,
    pendingInvitations: memberships.filter((m) => m.status === 'INVITED'),
    switchClinic,
    repairSetup,
    refreshGrants,
    reload: load,
  }), [
    state, memberships, capabilities, enterable, activeMembership, currency, timeZone, routeRef,
    defaultRef, access, error, switching, accessChanged, switchClinic, repairSetup, refreshGrants, load,
  ]);

  return (
    <ClinicCtx.Provider value={value}>
      <CurrencyProvider currency={currency} timeZone={timeZone}>{children}</CurrencyProvider>
    </ClinicCtx.Provider>
  );
}

export default ClinicContextProvider;
